import Alert from '@jg/alert';
import { withErrorBoundary } from '@jg/error-boundary';
import Loading from '@jg/loading';
import { useTracking } from '@jg/react-tracking';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ErrorPage from '../components/ErrorPage';
import Grid from '../components/Grid';
import GroupedResults from '../components/GroupedResults';
import GroupTitle from '../components/GroupTitle';
import HelpAndGuidanceCard from '../components/HelpAndGuidanceCard';
import NoResults from '../components/NoResults';
import Reveal from '../components/Reveal';
import SearchInput from '../components/SearchInput';
import Section from '../components/Section';
import helpIcon from '../icons/help.svg';
import captureError from '../lib/captureError';
import { useHelpAndGuidanceResults } from '../lib/helpAndGuidance';
import useCountryCode from '../lib/useCountryCode';
import useDocumentTitle from '../lib/useDocumentTitle';
import useHistory from '../lib/useHistory';
import { mergeQueryParams } from '../lib/useHistory/useHistory';
import useQueryParams from '../lib/useQueryParams';
import useRefresh from '../lib/useRefresh';
import useScrollRestoration from '../lib/useScrollRestoration';
import useSearchResults from '../lib/useSearchResults';
import './app.scss';

const App: React.FC = () => {
  const { t } = useTranslation();

  useDocumentTitle(t('documentTitle', 'Search - JustGiving'));

  const { location, push } = useHistory();

  const { restoreScroll } = useScrollRestoration();

  const { q, countryCode: countryCodeOverride } = useQueryParams(
    location.search
  );

  const [refreshToken, refresh] = useRefresh();

  // countryCode will be None while loading, and will always be Some(value)
  // once loaded, which may be async.
  const countryCode = useCountryCode(countryCodeOverride);

  // Use only the countryCode override (which may be None) for search API.
  // If this is None, then the search API will determine the country itself.
  const results = useSearchResults({
    q,
    countryCode: countryCodeOverride,
    refreshToken,
  });

  // Use the countryCode value for Help and Guidance, which will wait until
  // the value is Some(value).
  const helpAndGuidanceResults = useHelpAndGuidanceResults({ q, countryCode });

  // Restore the scroll position once we have everything loaded
  useEffect(() => {
    if (
      results.type === 'loaded' &&
      helpAndGuidanceResults.type === 'loaded' &&
      countryCode.isSome()
    ) {
      restoreScroll();
    }
  }, [results, helpAndGuidanceResults, countryCode, restoreScroll]);

  const baseTrack = useTracking();
  const track = useCallback(
    (data?: object) =>
      baseTrack({
        actor_id: q.map<string>(x => x).getOrElse(''),
        actor_sources: 'search string',
        ...data,
      }),
    [baseTrack, q]
  );

  // Track the page view event each time q changes
  useEffect(() => {
    track({ event: 'page view' });
  }, [track]);

  // If countryCode has not yet resolved, hold fire
  if (countryCode.isNone()) return null;

  return (
    <>
      <SearchInput
        q={q.toNullable()}
        onSubmit={newQ => {
          if (q.toNullable() !== newQ) {
            push(
              mergeQueryParams(location, { q: newQ === '' ? undefined : newQ })
            );
          } else {
            refresh();
          }
        }}
      />

      <h1 className="jg-sr-only">
        {t('results.titleScreenReaderOnly', 'Search Results')}
      </h1>

      {helpAndGuidanceResults.type === 'loaded' &&
        helpAndGuidanceResults.data.length > 0 && (
          <Section topBorder={false}>
            <GroupTitle
              title={t(
                'resultGroup.helpAndGuidance.title',
                'Help and guidance'
              )}
              icon={helpIcon}
            />

            <Grid smCols={1} testId="jg-search-results--help-and-guidance">
              {helpAndGuidanceResults.data.map(
                (
                  {
                    url,
                    image,
                    title,
                    description,
                    category,
                    trackingZendeskId,
                  },
                  i
                ) => (
                  <HelpAndGuidanceCard
                    key={i}
                    {...{ url, image, title, description, category }}
                    onClick={() => {
                      track({
                        event: 'link click',
                        subtype: 'card',
                        page_section: 'help and guidance',
                        ...(trackingZendeskId
                          ? {
                              content_type: 'zendesk_id',
                              content_id: trackingZendeskId,
                            }
                          : {}),
                      });
                    }}
                  />
                )
              )}
            </Grid>
          </Section>
        )}

      {results.type === 'loading' && (
        <Reveal afterMs={500}>
          <Loading className="jg-space-mvlg" />
        </Reveal>
      )}

      {results.type === 'error' && (
        <div className="container jg-space-phms@md">
          <div className="jg-space-mtsm jg-space-mblg">
            <Alert
              type="error"
              displayType="inline"
              onClose={null as any /* @jg/alert bug */}
            >
              {t(
                'results.error',
                'We couldn’t fetch your search results right now, please try again'
              )}
            </Alert>
          </div>
        </div>
      )}

      {results.type === 'idle' && <div className="jg-space-pvlg" />}

      {results.type === 'loaded' && results.data.results.Total === 0 && (
        <NoResults q={q} />
      )}

      {results.type === 'loaded' &&
        results.data.results.GroupedResults.map(groupedResults => (
          <Section
            topBorder={
              helpAndGuidanceResults.type === 'loaded' &&
              helpAndGuidanceResults.data.length > 0
            }
            key={groupedResults.Title}
          >
            <GroupedResults
              groupedResults={groupedResults}
              loadMore={results.data.loadMore}
              paginationState={
                results.data.paginationStates[groupedResults.Specific] || 'idle'
              }
              track={track}
            />
          </Section>
        ))}
    </>
  );
};

export default withErrorBoundary(App, <ErrorPage />, captureError);
